// stores/demoMode.js
// Demo mode store for portfolio showcase - allows exploring the app without a backend
import { writable, get } from 'svelte/store';

const DEMO_KEY = 'demoMode';

// Only enable demo mode when explicitly set in localStorage or on Vercel production
function shouldAutoEnableDemo() {
  if (typeof window === 'undefined') return false;

  // Check if explicitly set in localStorage
  const explicitlySet = localStorage.getItem(DEMO_KEY);
  if (explicitlySet === 'true') return true;
  if (explicitlySet === 'false') return false;

  // Check environment variable
  if (import.meta.env.VITE_DEMO_MODE === 'true') return true;

  // Auto-enable on Vercel deployments (production demo)
  if (window.location.hostname.includes('vercel.app')) return true;

  // Default: do NOT auto-enable on localhost - let users choose
  return false;
}

function createDemoStore() {
  const isDemo = shouldAutoEnableDemo();
  const { subscribe, set, update } = writable(isDemo);

  return {
    subscribe,

    enable() {
      if (typeof window !== 'undefined') {
        localStorage.setItem(DEMO_KEY, 'true');
      }
      set(true);
    },

    disable() {
      if (typeof window !== 'undefined') {
        localStorage.setItem(DEMO_KEY, 'false');
      }
      set(false);
    },

    isEnabled() {
      return get({ subscribe });
    }
  };
}

export const demoMode = createDemoStore();

// Demo user data
export const DEMO_USER = {
  username: 'Demo User',
  email: 'demo@example.com',
  role: 'Administrator'
};

// Demo mock data for showcasing the application
export const DEMO_DATA = {
  clients: [
    {
      id: 1,
      name: 'Acme Corporation',
      email: 'contact@acme.com',
      phone: '+1 (555) 123-4567',
      address: { street: '123 Business Ave', city: 'San Francisco', state: 'CA', zip: '94105' },
      created_at: '2024-01-15T10:00:00Z',
      projects_count: 3
    },
    {
      id: 2,
      name: 'TechStart Inc.',
      email: 'hello@techstart.io',
      phone: '+1 (555) 987-6543',
      address: { street: '456 Innovation Blvd', city: 'Austin', state: 'TX', zip: '78701' },
      created_at: '2024-02-20T14:30:00Z',
      projects_count: 2
    },
    {
      id: 3,
      name: 'Green Building Co.',
      email: 'info@greenbuilding.com',
      phone: '+1 (555) 456-7890',
      address: { street: '789 Eco Lane', city: 'Portland', state: 'OR', zip: '97201' },
      created_at: '2024-03-10T09:15:00Z',
      projects_count: 4
    },
    {
      id: 4,
      name: 'Metro Property Management',
      email: 'admin@metropm.com',
      phone: '+1 (555) 321-0987',
      address: { street: '321 Urban Plaza', city: 'Chicago', state: 'IL', zip: '60601' },
      created_at: '2024-04-05T11:45:00Z',
      projects_count: 5
    }
  ],

  workers: [
    {
      id: 1,
      name: 'John Martinez',
      email: 'john.m@workflow.com',
      phone: '+1 (555) 111-2222',
      skills: ['Electrical', 'HVAC', 'Plumbing'],
      hourly_rate: 75.00,
      availability_status: 'AVAILABLE',
      created_at: '2024-01-10T08:00:00Z'
    },
    {
      id: 2,
      name: 'Sarah Chen',
      email: 'sarah.c@workflow.com',
      phone: '+1 (555) 333-4444',
      skills: ['Project Management', 'Quality Control', 'Safety Inspection'],
      hourly_rate: 85.00,
      availability_status: 'BUSY',
      created_at: '2024-01-12T09:30:00Z'
    },
    {
      id: 3,
      name: 'Mike Johnson',
      email: 'mike.j@workflow.com',
      phone: '+1 (555) 555-6666',
      skills: ['Carpentry', 'Framing', 'Finishing'],
      hourly_rate: 65.00,
      availability_status: 'AVAILABLE',
      created_at: '2024-02-01T10:00:00Z'
    },
    {
      id: 4,
      name: 'Emily Rodriguez',
      email: 'emily.r@workflow.com',
      phone: '+1 (555) 777-8888',
      skills: ['Painting', 'Drywall', 'Flooring'],
      hourly_rate: 55.00,
      availability_status: 'ON_LEAVE',
      created_at: '2024-02-15T11:30:00Z'
    },
    {
      id: 5,
      name: 'David Kim',
      email: 'david.k@workflow.com',
      phone: '+1 (555) 999-0000',
      skills: ['Roofing', 'Waterproofing', 'Insulation'],
      hourly_rate: 70.00,
      availability_status: 'AVAILABLE',
      created_at: '2024-03-01T08:45:00Z'
    }
  ],

  projects: [
    {
      id: 1,
      name: 'Office Renovation - Acme HQ',
      description: 'Complete renovation of 3rd floor office space including new electrical, HVAC upgrades, and modern finishes.',
      client: 1,
      client_name: 'Acme Corporation',
      status: 'IN_PROGRESS',
      budget: 150000.00,
      start_date: '2024-06-01',
      end_date: '2024-09-30',
      progress: 65,
      created_at: '2024-05-15T10:00:00Z'
    },
    {
      id: 2,
      name: 'TechStart New Office Build-out',
      description: 'Complete build-out of new 5,000 sq ft office space with open floor plan, conference rooms, and break area.',
      client: 2,
      client_name: 'TechStart Inc.',
      status: 'PLANNING',
      budget: 280000.00,
      start_date: '2024-10-01',
      end_date: '2025-02-28',
      progress: 10,
      created_at: '2024-08-01T14:00:00Z'
    },
    {
      id: 3,
      name: 'Green Building Solar Installation',
      description: 'Installation of 100kW solar panel system with battery storage and monitoring.',
      client: 3,
      client_name: 'Green Building Co.',
      status: 'IN_PROGRESS',
      budget: 95000.00,
      start_date: '2024-07-15',
      end_date: '2024-10-15',
      progress: 40,
      created_at: '2024-06-20T09:00:00Z'
    },
    {
      id: 4,
      name: 'Metro Apartment Complex Repairs',
      description: 'Comprehensive repairs across 50-unit apartment complex including plumbing, electrical, and common areas.',
      client: 4,
      client_name: 'Metro Property Management',
      status: 'IN_PROGRESS',
      budget: 320000.00,
      start_date: '2024-05-01',
      end_date: '2024-12-31',
      progress: 55,
      created_at: '2024-04-10T11:00:00Z'
    },
    {
      id: 5,
      name: 'Acme Warehouse Expansion',
      description: 'Expansion of existing warehouse by 10,000 sq ft with loading docks and climate control.',
      client: 1,
      client_name: 'Acme Corporation',
      status: 'COMPLETED',
      budget: 450000.00,
      start_date: '2024-01-15',
      end_date: '2024-05-30',
      progress: 100,
      created_at: '2023-12-01T10:00:00Z'
    }
  ],

  workOrders: [
    {
      id: 1,
      title: 'Electrical Panel Upgrade',
      description: 'Upgrade main electrical panel from 200A to 400A service to support new HVAC equipment.',
      project: 1,
      project_name: 'Office Renovation - Acme HQ',
      status: 'IN_PROGRESS',
      priority: 'HIGH',
      estimated_hours: 16,
      actual_hours: 8,
      scheduled_start: '2024-08-15T08:00:00Z',
      scheduled_end: '2024-08-17T17:00:00Z',
      assigned_workers: [1],
      created_at: '2024-08-10T10:00:00Z'
    },
    {
      id: 2,
      title: 'HVAC Ductwork Installation',
      description: 'Install new ductwork for 4-zone HVAC system throughout 3rd floor.',
      project: 1,
      project_name: 'Office Renovation - Acme HQ',
      status: 'PENDING',
      priority: 'MEDIUM',
      estimated_hours: 40,
      actual_hours: 0,
      scheduled_start: '2024-08-20T08:00:00Z',
      scheduled_end: '2024-08-28T17:00:00Z',
      assigned_workers: [1, 3],
      created_at: '2024-08-10T10:30:00Z'
    },
    {
      id: 3,
      title: 'Solar Panel Mounting',
      description: 'Install mounting brackets and rails for 250 solar panels on warehouse roof.',
      project: 3,
      project_name: 'Green Building Solar Installation',
      status: 'IN_PROGRESS',
      priority: 'HIGH',
      estimated_hours: 32,
      actual_hours: 20,
      scheduled_start: '2024-08-12T07:00:00Z',
      scheduled_end: '2024-08-16T16:00:00Z',
      assigned_workers: [5],
      created_at: '2024-08-05T09:00:00Z'
    },
    {
      id: 4,
      title: 'Unit 15 Plumbing Repair',
      description: 'Replace corroded pipes under kitchen sink and repair water damage.',
      project: 4,
      project_name: 'Metro Apartment Complex Repairs',
      status: 'COMPLETED',
      priority: 'URGENT',
      estimated_hours: 4,
      actual_hours: 5,
      scheduled_start: '2024-08-10T09:00:00Z',
      scheduled_end: '2024-08-10T14:00:00Z',
      assigned_workers: [1],
      created_at: '2024-08-09T16:00:00Z'
    },
    {
      id: 5,
      title: 'Conference Room A Painting',
      description: 'Prep and paint Conference Room A with premium low-VOC paint, accent wall in company colors.',
      project: 1,
      project_name: 'Office Renovation - Acme HQ',
      status: 'PENDING',
      priority: 'LOW',
      estimated_hours: 12,
      actual_hours: 0,
      scheduled_start: '2024-09-01T08:00:00Z',
      scheduled_end: '2024-09-03T17:00:00Z',
      assigned_workers: [4],
      created_at: '2024-08-12T11:00:00Z'
    },
    {
      id: 6,
      title: 'Quality Inspection - Phase 1',
      description: 'Comprehensive quality inspection of completed electrical and structural work.',
      project: 1,
      project_name: 'Office Renovation - Acme HQ',
      status: 'IN_PROGRESS',
      priority: 'HIGH',
      estimated_hours: 8,
      actual_hours: 3,
      scheduled_start: '2024-08-14T09:00:00Z',
      scheduled_end: '2024-08-14T17:00:00Z',
      assigned_workers: [2],
      created_at: '2024-08-13T08:00:00Z'
    },
    {
      id: 7,
      title: 'Common Area Flooring',
      description: 'Install new LVP flooring in building lobby and hallways (2,500 sq ft).',
      project: 4,
      project_name: 'Metro Apartment Complex Repairs',
      status: 'PENDING',
      priority: 'MEDIUM',
      estimated_hours: 24,
      actual_hours: 0,
      scheduled_start: '2024-08-25T07:00:00Z',
      scheduled_end: '2024-08-29T16:00:00Z',
      assigned_workers: [4, 3],
      created_at: '2024-08-14T14:00:00Z'
    },
    {
      id: 8,
      title: 'Roof Waterproofing',
      description: 'Apply waterproof membrane to flat roof sections after solar installation prep.',
      project: 3,
      project_name: 'Green Building Solar Installation',
      status: 'COMPLETED',
      priority: 'HIGH',
      estimated_hours: 16,
      actual_hours: 14,
      scheduled_start: '2024-08-05T07:00:00Z',
      scheduled_end: '2024-08-07T16:00:00Z',
      assigned_workers: [5],
      created_at: '2024-08-01T10:00:00Z'
    }
  ]
};

// Helper function to get mock data by type
export function getMockData(type) {
  return DEMO_DATA[type] || [];
}

// Helper to simulate API delay for realistic feel
export function simulateDelay(ms = 300) {
  return new Promise(resolve => setTimeout(resolve, ms));
}
